import axios from 'axios'
import { AlertCircle, CheckCircle, Loader, Save, TestTube, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8888'

interface UserCloudConfig {
  id: number
  provider: string
  model: string
  endpoint: string | null
  created_at: string
  updated_at: string
}

export default function LLMConfig() {
  const [provider, setProvider] = useState('gemini')
  const [apiKey, setApiKey] = useState('')
  const [model, setModel] = useState('')
  const [endpoint, setEndpoint] = useState('')
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState(false)
  const [config, setConfig] = useState<UserCloudConfig | null>(null)
  const [configured, setConfigured] = useState(false)

  // Provider models
  const providerModels: Record<string, string[]> = {
    gemini: ['gemini-2.0-flash-exp', 'gemini-1.5-pro', 'gemini-1.5-flash'],
    openai: ['gpt-4o', 'gpt-4-turbo', 'gpt-3.5-turbo'],
    mistral: ['mistral-large-latest', 'mistral-medium-latest', 'mistral-small-latest'],
    anthropic: ['claude-3-5-sonnet-20241022', 'claude-3-opus-20240229', 'claude-3-haiku-20240307'],
    groq: ['llama-3.3-70b-versatile', 'mixtral-8x7b-32768', 'gemma2-9b-it'],
    cohere: ['command-r-plus', 'command-r', 'command']
  }

  useEffect(() => {
    void loadConfig()
  }, [])

  useEffect(() => {
    // Set default model when provider changes
    if (providerModels[provider]) {
      setModel(providerModels[provider][0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider])

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/api/llm/config/user-cloud`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (response.data.configured) {
        const configData = response.data.config as UserCloudConfig
        setConfig(configData)
        setConfigured(true)
        setProvider(configData.provider)
        setModel(configData.model)
        setEndpoint(configData.endpoint || '')
      }
    } catch (error) {
      console.error('Failed to load config:', error)
    }
  }

  const handleSave = async () => {
    if (!apiKey.trim()) {
      toast.error('Please enter your API key')
      return
    }

    if (!model.trim()) {
      toast.error('Please select a model')
      return
    }

    setLoading(true)

    try {
      const token = localStorage.getItem('token')
      await axios.post(
        `${API_URL}/api/llm/config/user-cloud`,
        {
          provider,
          api_key: apiKey,
          model_name: model,
          endpoint: endpoint || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      toast.success('Configuration saved successfully')
      setApiKey('') // Clear API key from form
      await loadConfig()
    } catch (error) {
      const err = error as { response?: { data?: { detail?: string } } }
      toast.error(err.response?.data?.detail || 'Failed to save configuration')
    } finally {
      setLoading(false)
    }
  }

  const handleTest = async () => {
    if (!configured) {
      toast.error('Please save your configuration first')
      return
    }

    setTesting(true)

    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(
        `${API_URL}/api/llm/config/user-cloud/test`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      if (response.data.success) {
        toast.success('API key is valid and working!')
      } else {
        toast.error(response.data.message as string)
      }
    } catch (error) {
      const err = error as { response?: { data?: { detail?: string } } }
      toast.error(err.response?.data?.detail || 'Test failed')
    } finally {
      setTesting(false)
    }
  }

  const handleDelete = async () => {
    // eslint-disable-next-line no-alert
    if (!confirm('Are you sure you want to delete your API configuration?')) {
      return
    }

    setLoading(true)

    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/api/llm/config/user-cloud`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      toast.success('Configuration deleted')
      setConfigured(false)
      setConfig(null)
      setApiKey('')
    } catch (error) {
      const err = error as { response?: { data?: { detail?: string } } }
      toast.error(err.response?.data?.detail || 'Failed to delete configuration')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--body-bg)', padding: '40px 20px' }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            LLM Configuration
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            Use your own API keys for LLM inference. Cost: $0 (you pay provider directly)
          </p>
        </div>

        {/* Current Config Status */}
        {configured && config && (
          <div
            className="p-6 rounded-lg mb-6"
            style={{
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)'
            }}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={20} style={{ color: '#10b981' }} />
                  <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Configuration Active
                  </h3>
                </div>
                <div className="space-y-1" style={{ color: 'var(--text-secondary)' }}>
                  <p>Provider: <span style={{ color: 'var(--text-primary)' }}>{config.provider}</span></p>
                  <p>Model: <span style={{ color: 'var(--text-primary)' }}>{config.model}</span></p>
                  {config.endpoint && (
                    <p>Endpoint: <span style={{ color: 'var(--text-primary)' }}>{config.endpoint}</span></p>
                  )}
                  <p className="text-sm">Last updated: {new Date(config.updated_at).toLocaleString()}</p>
                </div>
              </div>
              <button
                onClick={() => void handleTest()}
                disabled={testing}
                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
                style={{
                  background: 'var(--primary-color)',
                  color: 'white'
                }}
              >
                {testing ? <Loader size={16} className="animate-spin" /> : <TestTube size={16} />}
                Test
              </button>
            </div>
          </div>
        )}

        {/* Config Form */}
        <div
          className="p-6 rounded-lg"
          style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--border-color)'
          }}
        >
          <h2 className="text-xl font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>
            {configured ? 'Update Configuration' : 'Configure Your API'}
          </h2>

          <div className="space-y-6">
            {/* Provider Selection */}
            <div>
              <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                Provider
              </label>
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                className="w-full px-4 py-3 rounded-lg"
                style={{
                  background: 'var(--input-bg)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
              >
                <option value="gemini">Google Gemini</option>
                <option value="openai">OpenAI</option>
                <option value="mistral">Mistral AI</option>
                <option value="anthropic">Anthropic Claude</option>
                <option value="groq">Groq</option>
                <option value="cohere">Cohere</option>
              </select>
            </div>

            {/* API Key */}
            <div>
              <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                API Key
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={configured ? 'Enter new API key to update' : 'Enter your API key'}
                className="w-full px-4 py-3 rounded-lg"
                style={{
                  background: 'var(--input-bg)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
              />
              <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                Your API key is encrypted and stored securely
              </p>
            </div>

            {/* Model Selection */}
            <div>
              <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                Model
              </label>
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="w-full px-4 py-3 rounded-lg"
                style={{
                  background: 'var(--input-bg)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
              >
                {providerModels[provider]?.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom Endpoint (Optional) */}
            {provider === 'openai' && (
              <div>
                <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>
                  Custom Endpoint (Optional)
                </label>
                <input
                  type="text"
                  value={endpoint}
                  onChange={(e) => setEndpoint(e.target.value)}
                  placeholder="https://api.openai.com/v1"
                  className="w-full px-4 py-3 rounded-lg"
                  style={{
                    background: 'var(--input-bg)',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-primary)'
                  }}
                />
                <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  For OpenAI-compatible APIs (e.g., Azure OpenAI, local LLM)
                </p>
              </div>
            )}

            {/* Info Box */}
            <div
              className="p-4 rounded-lg flex items-start gap-3"
              style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)'
              }}
            >
              <AlertCircle size={20} style={{ color: '#3b82f6', flexShrink: 0, marginTop: '2px' }} />
              <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                <p className="font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  Cost: $0 (You pay provider directly)
                </p>
                <p>
                  Your API key is used to call the provider&apos;s API directly. You will be charged by the provider
                  based on your usage. RunAI does not charge any fees for this mode.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => void handleSave()}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors"
                style={{
                  background: 'var(--primary-color)',
                  color: 'white'
                }}
              >
                {loading ? <Loader size={20} className="animate-spin" /> : <Save size={20} />}
                {configured ? 'Update Configuration' : 'Save Configuration'}
              </button>

              {configured && (
                <button
                  onClick={() => void handleDelete()}
                  disabled={loading}
                  className="px-6 py-3 rounded-lg font-medium transition-colors"
                  style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    color: '#ef4444',
                    border: '1px solid rgba(239, 68, 68, 0.3)'
                  }}
                >
                  <Trash2 size={20} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
